import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Chip,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { FilterParams } from '@/shared/api';

interface DashboardFiltersProps {
  filters: FilterParams;
  onFiltersChange: (filters: FilterParams) => void;
  onApplyFilters: () => void;
  loading?: boolean;
}

const DashboardFilters: React.FC<DashboardFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  loading = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleFilterChange = (key: keyof FilterParams, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
    onApplyFilters();
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== undefined && value !== '');

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== undefined && value !== '').length;
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: expanded ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="h6" fontWeight="600">
            Filtros
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${getActiveFiltersCount()} activo${getActiveFiltersCount() > 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasActiveFilters && (
            <Tooltip title="Limpiar filtros">
              <IconButton size="small" onClick={clearFilters}>
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Date Range */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Rango de Fechas
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <DatePicker
                label="Fecha de inicio"
                value={filters.start_date ? dayjs(filters.start_date) : null}
                onChange={(date) => handleFilterChange('start_date', date?.format('YYYY-MM-DD'))}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="Fecha de fin"
                value={filters.end_date ? dayjs(filters.end_date) : null}
                onChange={(date) => handleFilterChange('end_date', date?.format('YYYY-MM-DD'))}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              disabled={!hasActiveFilters || loading}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              onClick={onApplyFilters}
              disabled={loading}
              startIcon={<FilterIcon />}
            >
              {loading ? 'Aplicando...' : 'Aplicar Filtros'}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export { DashboardFilters };
